import { useId, type ElementType, type ReactNode } from 'react';
import { juntarClasses } from './classes';
import type { Densidade } from './tipos';

export type VarianteCartao = 'elevado' | 'vidro' | 'contorno';

/**
 * Superfície de conteúdo.
 *
 * `vidro` é o glassmorphism de §8 — e é sutil de propósito: o desfoque atrás de
 * uma tabela de 12 colunas atrapalha a leitura. Use vidro em painel que flutua
 * sobre outro conteúdo (cabeçalho fixo, resumo sobre gráfico, gaveta); use
 * `elevado` para o A4 de dados.
 */
const VARIANTES: Readonly<Record<VarianteCartao, string>> = {
  // `shadow-cartao`/`shadow-elevada` saem de `--deca-sombra`. Eram `shadow-sm` e
  // `shadow-lg` do Tailwind — preto com alfa fixo, a única cor dos componentes
  // que ainda ficava de fora do contrato de token.
  elevado: 'border border-borda bg-superficie-elevada shadow-cartao',
  vidro: 'border border-borda/70 bg-superficie-vidro/60 backdrop-blur-vidro shadow-elevada',
  contorno: 'border border-borda bg-transparent',
};

const PREENCHIMENTO: Readonly<Record<Densidade, string>> = {
  compacta: 'p-4',
  confortavel: 'p-6',
};

export interface PropsCartao {
  children: ReactNode;
  /** Vira `<h3>` e nomeia a região via `aria-labelledby`. */
  titulo?: ReactNode;
  subtitulo?: ReactNode;
  /** Ações do cabeçalho — botões, filtro, menu. */
  acao?: ReactNode;
  rodape?: ReactNode;
  variante?: VarianteCartao;
  densidade?: Densidade;
  /** Elemento raiz. `section`/`article` quando o cartão é uma região de conteúdo. */
  como?: 'div' | 'section' | 'article' | 'li';
  /** Só para layout (largura, span de grid, margem). */
  className?: string;
  /** Remove o preenchimento do corpo — para cartão que embrulha uma tabela. */
  corpoSemPreenchimento?: boolean;
}

export function Cartao({
  children,
  titulo,
  subtitulo,
  acao,
  rodape,
  variante = 'elevado',
  densidade = 'confortavel',
  como = 'div',
  className,
  corpoSemPreenchimento = false,
}: PropsCartao): JSX.Element {
  const Raiz = como as ElementType;
  const idTitulo = useId();
  const temCabecalho = titulo !== undefined || subtitulo !== undefined || acao !== undefined;

  return (
    <Raiz
      className={juntarClasses(
        'flex flex-col overflow-hidden rounded-painel',
        VARIANTES[variante],
        className,
      )}
      aria-labelledby={titulo !== undefined ? idTitulo : undefined}
    >
      {temCabecalho && (
        <header
          className={juntarClasses(
            'flex items-start justify-between gap-4',
            PREENCHIMENTO[densidade],
            'pb-0',
          )}
        >
          <div className="min-w-0">
            {titulo !== undefined && (
              <h3
                id={idTitulo}
                className="truncate font-display text-base font-semibold tracking-tight text-conteudo-primario"
              >
                {titulo}
              </h3>
            )}
            {subtitulo !== undefined && (
              <p className="mt-1 text-sm text-conteudo-secundario">{subtitulo}</p>
            )}
          </div>
          {acao !== undefined && <div className="shrink-0">{acao}</div>}
        </header>
      )}

      <div
        className={juntarClasses(
          'min-w-0 flex-1',
          corpoSemPreenchimento ? '' : PREENCHIMENTO[densidade],
          temCabecalho && !corpoSemPreenchimento && 'pt-4',
        )}
      >
        {children}
      </div>

      {rodape !== undefined && (
        <footer
          className={juntarClasses(
            'border-t border-borda bg-superficie-base/40',
            densidade === 'compacta' ? 'px-4 py-3' : 'px-6 py-4',
          )}
        >
          {rodape}
        </footer>
      )}
    </Raiz>
  );
}

/**
 * Cartão de indicador — o "número que sobe" do dashboard (§8.1).
 *
 * Não formata nada: recebe o valor já pronto (use `ValorFiscal` no `valor`).
 * Assim o mesmo cartão serve para moeda, percentual e contagem.
 */
export interface PropsCartaoIndicador {
  rotulo: string;
  valor: ReactNode;
  /** Comparação, período ou origem do número. */
  detalhe?: ReactNode;
  icone?: ReactNode;
  variante?: VarianteCartao;
  /**
   * O cartão abre o que está por trás do número ("A receber" → as parcelas).
   * Vira botão, e `acao` diz o que o clique mostra.
   */
  aoClicar?: () => void;
  /** O convite ao clique, no pé do cartão: "Ver as parcelas". Só com `aoClicar`. */
  acao?: string;
  className?: string;
}

export function CartaoIndicador({
  rotulo,
  valor,
  detalhe,
  icone,
  variante = 'elevado',
  aoClicar,
  acao,
  className,
}: PropsCartaoIndicador): JSX.Element {
  const conteudo = (
    <>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium uppercase tracking-wide text-conteudo-secundario">
          {rotulo}
        </p>
        {icone !== undefined && <span className="text-conteudo-secundario">{icone}</span>}
      </div>
      <div className="font-display text-2xl font-semibold tracking-tight">{valor}</div>
      {detalhe !== undefined && <p className="text-xs text-conteudo-secundario">{detalhe}</p>}
      {aoClicar !== undefined && acao !== undefined && (
        <span className="mt-auto pt-1 text-xs font-medium text-acento-ciano">{acao} →</span>
      )}
    </>
  );
  const classes = juntarClasses(
    'flex flex-col gap-2 rounded-painel p-5',
    VARIANTES[variante],
    className,
  );

  if (aoClicar === undefined) return <div className={classes}>{conteudo}</div>;
  return (
    <button
      type="button"
      onClick={aoClicar}
      className={juntarClasses(
        classes,
        'text-left transition-colors hover:border-acento-ciano/50 hover:bg-superficie-vidro/60',
      )}
    >
      {conteudo}
    </button>
  );
}
